import React from 'react';
import styled from 'styled-components';
import { 
  MEMES, COMIC_BOOK, VIDEOS, TITLES, ROADMAP, 
  ContractAddress, Buy, Socials, 
  ORIGINAL_ART, NDHAUS_PHOTO, TitleArt, Lettering, C, hover, EASE_OUT, DrawnNav
} from '../kit';
import { FACTS } from '../d/Tokenomics';
import { CONTENT } from '../content';
import { useTokenomics } from '../useTokenomics';
import { SupplyDonut } from '../kit';

/* ------------------------------------------------------------- Header */
const M5_NAV = [
  { label: 'HOME', href: '#top' },
  { label: 'DOLLARIZE', href: '#dollarize' },
  { label: 'TOKENOMICS', href: '#tokenomics' },
  { label: 'ROADMAP', href: '#roadmap' }
];

export const M5Header = () => (
  <HeaderContainer id="top">
    <div className="nav-wrapper">
      <DrawnNav items={M5_NAV} />
    </div>
    
    <div className="hero-titles">
      <TitleArt src={TITLES.zeus.src} alt={TITLES.zeus.alt} $w="90%" $tilt={2} className="zeus-title" />
    </div>

    <div className="ca-chip">
      <ContractAddress tone="light" />
    </div>
    <div className="buy-btn">
      <Buy big />
    </div>
  </HeaderContainer>
);

const HeaderContainer = styled.header`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px 40px;
  background-image: url('/redesign2/scenes/camp.webp');
  background-size: cover;
  background-position: center bottom;
  min-height: 85dvh;
  border-bottom: 4px solid ${C.ink};

  .nav-wrapper {
    width: 100%;
    margin-bottom: 10px;
  }

  .hero-titles {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 10%;
    gap: 10px;
  }
  
  .zeus-title {
    margin-top: -10px;
  }

  .ca-chip {
    margin-top: auto;
    margin-bottom: 20px;
    z-index: 10;
  }

  .buy-btn {
    z-index: 10;
  }
`;

/* ------------------------------------------------------------- About */
export const M5About = () => (
  <SectionContainer id="about">
    <div className="video-wrap">
      <video src={VIDEOS.trailer.src} poster={VIDEOS.trailer.poster} controls playsInline loop preload="metadata" />
    </div>
    <div className="text-content">
      {CONTENT.about.paragraphs.map((p, i) => {
        if (i === CONTENT.about.paragraphs.length - 1) {
          const [before] = p.split('has officially secured trademark');
          return (
            <p key={i}>
              {before}
              <a href={CONTENT.about.trademarkLink} target="_blank" rel="noopener noreferrer">
                has officially secured trademark protection
              </a>
              {' '}over the $ZEUS name...
            </p>
          );
        }
        return <p key={i}>{p}</p>;
      })}
    </div>
  </SectionContainer>
);

/* ------------------------------------------------------------- Takeover */
export const M5Takeover = () => (
  <SectionContainer id="takeover" $bg={C.mist}>
    <TitleArt src={TITLES.takeOver.src} alt={TITLES.takeOver.alt} $w="90%" />
    <div className="video-wrap">
      <video src={VIDEOS.buy.src} poster={VIDEOS.buy.poster} controls playsInline loop preload="metadata" />
    </div>
    <div className="text-content">
      {CONTENT.aboutCto.paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  </SectionContainer>
);

/* ------------------------------------------------------------- Tokenomics */
export const M5Tokenomics = () => (
  <SectionContainer id="tokenomics" $bg={C.yellow}>
    <TitleArt src={TITLES.tokenomics.src} alt={TITLES.tokenomics.alt} $w="90%" />
    <div className="donut-wrap">
      <SupplyDonut size={240} />
    </div>
    <div className="treasure-scatter">
      {FACTS.map((f, i) => (
        <a key={f.key} href={f.link} target="_blank" rel="noopener noreferrer" className="treasure-item">
          <img src={f.art} alt="" className="treasure-art" />
          <Lettering $size="26px" $tilt={i % 2 === 0 ? -2 : 2}>{f.big}</Lettering>
          <span className="fact-label">{f.label}</span>
        </a>
      ))}
    </div>
  </SectionContainer>
);

/* ------------------------------------------------------------- Dollarize */
export const M5Dollarize = () => (
  <SectionContainer id="dollarize" $bg={C.ink} $color={C.white}>
    <img src="/redesign2/scenes/blackhole.webp" alt="Blackhole" className="hero-img" />
    <Lettering $size="32px">{CONTENT.dollarize.title}</Lettering>
    <div className="text-content">
      {CONTENT.dollarize.paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
    <div className="btn-row">
      <ActionBtn href={CONTENT.dollarize.mintLink} target="_blank" rel="noopener noreferrer">MINT</ActionBtn>
      <ActionBtn href={CONTENT.dollarize.howLink} target="_blank" rel="noopener noreferrer" $outline>HOW IT WORKS</ActionBtn>
    </div>
  </SectionContainer>
);

/* ------------------------------------------------------------- Roadmap */
export const M5Roadmap = () => {
  const first = ROADMAP[0];
  const last = ROADMAP[ROADMAP.length - 1];
  const middle = ROADMAP.slice(1, ROADMAP.length - 1);
  return (
    <SectionContainer id="roadmap" $bg={C.paper}>
      <TitleArt src={TITLES.roadmap.src} alt={TITLES.roadmap.alt} $w="80%" />
      <ol className="road-path">
        <li className="planned">
          <div className="dot"/>
          <div className="content">
            <span className="pill">{last.pill}</span>
            <h3>{last.lines.map((l, i) => <span key={i}>{l}</span>)}</h3>
            <p>{last.milestone.description}</p>
          </div>
        </li>
        <li className="folded">
          <div className="line-dashed" />
          <details className="fold-details">
            <summary>View past milestones</summary>
            <div className="fold-content">
              {middle.map((m, i) => (
                <div key={i} className="middle-item">
                  <span className="pill">{m.pill}</span>
                  <h4>{m.lines.join(' ')}</h4>
                </div>
              ))}
            </div>
          </details>
        </li>
        <li className="done">
          <div className="dot"/>
          <div className="content">
            <span className="pill">{first.pill}</span>
            <h3>{first.lines.map((l, i) => <span key={i}>{l}</span>)}</h3>
            <p>{first.milestone.description}</p>
          </div>
        </li>
      </ol>
    </SectionContainer>
  );
};

/* ------------------------------------------------------------- Artist */
export const M5Artist = () => (
  <SectionContainer id="artist" $bg={C.light}>
    <TitleArt src={TITLES.artist.src} alt={TITLES.artist.alt} $w="80%" />
    <img src={NDHAUS_PHOTO} alt="ndhaus" className="hero-img artist-img" />
    <div className="text-content">
      {CONTENT.artist.paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
    <img src={ORIGINAL_ART} alt="Original Zeus Art" className="hero-img original-img" />
  </SectionContainer>
);

/* ------------------------------------------------------------- NFTs */
export const M5Nfts = () => (
  <SectionContainer id="nfts" $bg={C.ink} $color={C.white}>
    <Lettering $size="32px">{CONTENT.nfts.title}</Lettering>
    <div className="text-content">
      {CONTENT.nfts.paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
    <img src="/redesign2/nfts/hero.webp" alt="Dreamscape Hero" className="hero-img" />
    <div className="nft-grid">
      {CONTENT.nfts.items.slice(0, 4).map(id => (
        <img key={id} src={`/redesign2/nfts/${id}.webp`} alt="" />
      ))}
    </div>
    <ActionBtn href={CONTENT.nfts.link} target="_blank" rel="noopener noreferrer">VIEW ON OPENSEA</ActionBtn>
  </SectionContainer>
);

/* ------------------------------------------------------------- Memes */
export const M5Memes = () => (
  <SectionContainer id="memes" $bg={C.sky}>
    <TitleArt src={TITLES.memes.src} alt={TITLES.memes.alt} $w="80%" />
    <div className="meme-scroll">
      {MEMES.map((m, i) => (
        <img key={i} src={m.src} alt={m.alt} loading="lazy" />
      ))}
    </div>
  </SectionContainer>
);

/* ------------------------------------------------------------- Comic */
export const M5Comic = ({ openComic }: { openComic: () => void }) => (
  <SectionContainer id="comic" $bg={C.paper}>
    <TitleArt src={TITLES.comic.src} alt={TITLES.comic.alt} $w="90%" />
    <p style={{textAlign: 'center', margin: '20px 0'}}>{CONTENT.comic.subtitle}</p>
    <img src={COMIC_BOOK[0].src} alt="Comic Cover" className="hero-img comic-cover" onClick={openComic} />
    <div className="btn-row">
      <ActionBtn as="button" onClick={openComic}>READ NOW</ActionBtn>
      <ActionBtn href="/zeus.pdf" download $outline>DOWNLOAD PDF</ActionBtn>
    </div>
  </SectionContainer>
);

/* ------------------------------------------------------------- Join & Footer */
export const M5Join = () => (
  <SectionContainer id="join" $bg={C.blue}>
    <img src="/redesign2/scenes/gang.webp" alt="The gang" className="hero-img gang-img" />
    <div className="btn-row">
      <Buy big />
    </div>
    <Socials color={C.ink} size={30} className="socials-bottom" />
  </SectionContainer>
);

export const M5Footer = () => (
  <FooterWrap>
    <img src="/assets/img/logo.webp" alt="Logo" className="logo" />
    <div className="links">
      <a href="/terms">Terms</a>
      <a href="/privacy">Privacy</a>
    </div>
  </FooterWrap>
);

/* ------------------------------------------------------------- Styles */

const SectionContainer = styled.section<{ $bg?: string; $color?: string }>`
  padding: 50px 20px;
  background: ${(p) => p.$bg || C.white};
  color: ${(p) => p.$color || C.ink};
  border-bottom: 4px solid ${C.ink};
  display: flex;
  flex-direction: column;
  align-items: center;

  .video-wrap {
    width: 100%;
    margin: 24px 0;
    border: 4px solid ${C.ink};
    border-radius: 16px;
    overflow: hidden;
    background: #000;
  }
  
  video {
    width: 100%;
    display: block;
  }

  .text-content {
    font-size: 18px;
    line-height: 1.5;
    margin-bottom: 24px;
    p { margin: 0 0 16px 0; }
    a { color: inherit; font-weight: bold; }
  }

  .hero-img {
    width: 100%;
    border: 4px solid ${C.ink};
    border-radius: 20px;
    margin-bottom: 24px;
    background: ${C.white};
  }

  .donut-wrap {
    margin: 30px 0;
  }

  .treasure-scatter {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px 12px;
    margin-top: 10px;
    width: 100%;
  }
  .treasure-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    text-decoration: none;
    color: ${C.ink};
    
    .treasure-art {
      width: 110px;
      height: auto;
      margin-bottom: -15px;
      z-index: 2;
      position: relative;
    }
    .fact-label { 
      font-family: 'GROBOLD', sans-serif; 
      font-size: 16px; 
      margin-top: 4px;
    }
  }

  .btn-row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
  }
  
  .road-path {
    list-style: none;
    padding: 0;
    margin: 20px 0 0;
    width: 100%;
    
    li {
      position: relative;
      padding: 0 0 30px 40px;
    }
    
    .dot {
      position: absolute;
      left: 0;
      top: 0;
      width: 24px;
      height: 24px;
      border: 3px solid ${C.ink};
      border-radius: 50%;
      background: ${C.yellow};
    }
    
    li.planned .dot { background: ${C.white}; }
    
    li:not(:last-child)::before {
      content: '';
      position: absolute;
      left: 10px;
      top: 24px;
      bottom: 0;
      width: 4px;
      background: ${C.ink};
    }
    
    li.folded::before {
      background: transparent;
      border-left: 4px dashed ${C.ink};
    }
    
    .fold-details {
      margin-left: -20px;
      padding: 10px 0;
    }
    .fold-details summary {
      cursor: pointer;
      font-weight: bold;
      color: ${C.ink};
    }
    .fold-content {
      margin-top: 10px;
      padding-left: 20px;
      border-left: 4px dashed rgba(14, 27, 51, 0.3);
    }
    .middle-item {
      margin-bottom: 16px;
      h4 { margin: 4px 0 0; font-family: 'GROBOLD', sans-serif; font-size: 16px; }
    }
    
    .pill {
      display: inline-block;
      padding: 2px 10px;
      background: ${C.yellow};
      border: 2px solid ${C.ink};
      border-radius: 999px;
      font-family: 'GROBOLD', sans-serif;
      font-size: 12px;
      margin-bottom: 8px;
    }
    
    h3 { margin: 0 0 8px; font-family: 'GROBOLD', sans-serif; }
    p { margin: 0; font-size: 16px; }
  }

  .nft-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 24px;
    img { width: 100%; border-radius: 12px; border: 3px solid ${C.ink}; }
  }
  
  .meme-scroll {
    display: flex;
    overflow-x: auto;
    gap: 16px;
    width: 100%;
    padding: 10px 0;
    snap-type: x mandatory;
    &::-webkit-scrollbar { display: none; }
    img {
      flex: 0 0 80%;
      scroll-snap-align: center;
      border: 4px solid ${C.ink};
      border-radius: 16px;
    }
  }

  .socials-bottom {
    margin-top: 30px;
  }
`;

const ActionBtn = styled.a<{ $outline?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 24px;
  font-family: 'GROBOLD', sans-serif;
  font-size: 18px;
  color: ${(p) => p.$outline ? C.ink : C.ink};
  background: ${(p) => p.$outline ? C.white : C.yellow};
  border: 3px solid ${C.ink};
  border-radius: 999px;
  text-decoration: none;
  box-shadow: 3px 4px 0 ${C.ink};
  cursor: pointer;
  
  &:active {
    transform: translate(2px, 2px);
    box-shadow: 1px 2px 0 ${C.ink};
  }
`;

const FooterWrap = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  background: ${C.ink};
  color: ${C.white};
  
  .logo { width: 60px; height: 60px; margin-bottom: 20px; }
  .links {
    display: flex;
    gap: 20px;
    a { color: ${C.white}; font-weight: bold; text-decoration: none; }
  }
`;
